import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';
import type { InvestorUserCredential } from '@/types/investor';

const log = createComponentLogger('InvestorCredentialService', 'investor-credentials');

const FUNCTION_NAME = 'investor-credentials';

export interface SaveCredentialInput {
  investorId: string;
  username?: string | null;
  notes?: string | null;
  /** Provide only when (re)setting the password — omit to keep the stored value. */
  password?: string;
  /** Explicitly clear the stored password. */
  clearPassword?: boolean;
}

/**
 * Client wrapper over the `investor-credentials` edge function (Manage Investors v2).
 * The function is the only path that encrypts/decrypts portal passwords and audits
 * admin reveals; this service never sees ciphertext.
 */
export class InvestorCredentialService {
  /** List credentials for an investor — own rows for users, all org rows for admins. No passwords. */
  static async list(investorId: string): Promise<InvestorUserCredential[]> {
    const { data, error } = await supabase.functions.invoke(FUNCTION_NAME, {
      body: { action: 'list', investorId }
    });
    if (error) {
      log.error('Failed to list investor credentials', { investorId }, error);
      throw error;
    }
    return (data?.credentials ?? []) as InvestorUserCredential[];
  }

  /** Upsert the current user's own credential. Preserves the stored password unless one is supplied. */
  static async saveMine(input: SaveCredentialInput): Promise<void> {
    const { error } = await supabase.functions.invoke(FUNCTION_NAME, {
      body: { action: 'save', ...input }
    });
    if (error) {
      log.error('Failed to save investor credential', { investorId: input.investorId }, error);
      throw error;
    }
  }

  /** Reveal (decrypt) a credential's password. Owner or strict admin only; admin reveals are audited. */
  static async reveal(credentialId: string): Promise<string | null> {
    const { data, error } = await supabase.functions.invoke(FUNCTION_NAME, {
      body: { action: 'reveal', credentialId }
    });
    if (error) {
      log.error('Failed to reveal investor credential', { credentialId }, error);
      throw error;
    }
    return (data?.password ?? null) as string | null;
  }
}
